/**
 * Permission Guard Component
 *
 * Conditionally renders children based on user permissions.
 * Useful for showing/hiding UI elements based on authorization.
 */

import { ReactNode } from 'react'
import { useAuthorization } from './authorizationHooks'

interface PermissionGuardProps {
  /** Single permission required */
  permission?: string

  /** Multiple permissions (requires ANY) */
  anyPermissions?: string[]

  /** Multiple permissions (requires ALL) */
  allPermissions?: string[]

  /** Role required */
  role?: string

  /** Content to render if user has permission */
  children: ReactNode

  /** Content to render if user lacks permission */
  fallback?: ReactNode

  /** If true, renders fallback instead of null when unauthorized */
  showFallback?: boolean
}

export function PermissionGuard({
  permission,
  anyPermissions,
  allPermissions,
  role,
  children,
  fallback = null,
  showFallback = false,
}: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, isLoading } = useAuthorization()

  // While loading, show nothing or fallback
  if (isLoading) {
    return showFallback ? <>{fallback}</> : null
  }

  // Check permissions
  let isAuthorized = false

  if (permission) {
    isAuthorized = hasPermission(permission)
  } else if (anyPermissions && anyPermissions.length > 0) {
    isAuthorized = hasAnyPermission(anyPermissions)
  } else if (allPermissions && allPermissions.length > 0) {
    isAuthorized = hasAllPermissions(allPermissions)
  } else if (role) {
    isAuthorized = hasRole(role)
  } else {
    // No permission/role specified - allow by default
    isAuthorized = true
  }

  if (!isAuthorized) {
    return showFallback ? <>{fallback}</> : null
  }

  return <>{children}</>
}

/**
 * Access Denied Component
 *
 * Default fallback for permission guards.
 */
export function AccessDenied({ message }: { message?: string }) {
  return (
    <div
      className="access-denied rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-8 text-center text-text-fg-error-strong dark:text-error-100"
    >
      <h3 className="mb-2 text-xl font-bold">Access Denied</h3>
      <p className="m-0">{message || 'You do not have permission to access this content.'}</p>
    </div>
  )
}

/**
 * Convenience components for common permission checks
 */

export function OrderCreateGuard({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <PermissionGuard permission="order:create" fallback={fallback}>
      {children}
    </PermissionGuard>
  )
}

export function ResultViewGuard({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <PermissionGuard permission="result:view" fallback={fallback}>
      {children}
    </PermissionGuard>
  )
}

export function DelegateGrantGuard({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <PermissionGuard permission="delegate:grant" fallback={fallback}>
      {children}
    </PermissionGuard>
  )
}

export function OrgAdminGuard({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <PermissionGuard role="org_admin" fallback={fallback}>
      {children}
    </PermissionGuard>
  )
}
